import { useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import AuthService from 'services/auth'
import CashFlow from 'services/CashFlow'
import { isCanceled, getApiErrorMessage, logApiError } from 'services/apiError'
import useAbortController from 'hooks/useAbortController'
import ItemTabs from 'components/ItemTabs'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import {
  ExpensesContainer,
  HintText,
  LoadingContainer,
  InfoText,
  WarningText
} from 'pages/Financial/Dre/styles'

// Sem teto as tabelas esticam com a resolução e as colunas de dia ficam largas demais em telas grandes.
const MAX_CONTENT_WIDTH = 1440
const SIDE_MARGIN = 16
const SUNDAY = 7
const SELECT_WIDTH = 260

// A semana de hoje quando o mês é o atual; senão, a primeira.
const defaultWeek = weeks => {
  const today = DateTime.now().toISODate()

  return (weeks.find(week => today >= week.start && today <= week.end) ?? weeks[0]).key
}

const hasMovement = cell => Object.values(cell).some(value => value !== 0)

/*
  O que as telas "Metas N°" e "Metas $" têm em comum: buscar as metas dia a dia do mês, escolher a
  categoria e a semana, e montar as seções (categoria → especialidade → profissional) para os dias
  dela, com todas as especialidades da categoria. Quem desenha a tabela recebe `sections`, com a
  categoria escolhida, e as `columns`: de segunda a sábado sempre, como na planilha; o domingo só
  quando teve meta ou atendimento.

  A requisição é do mês inteiro; trocar de categoria, semana ou percentual só refaz o recorte.
  A categoria escolhida vem de quem usa (`filter`), para continuar a mesma ao trocar entre
  "Metas N°" e "Metas $".
*/
export default function DailyGoals({ year, month, percentage, filter, onFilterChange, children }) {
  const businessId = AuthService.getBusinessId()
  const requestAuth = AuthService.getRequestAuth()
  const { abortarPendentes } = useAbortController()

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState(null)
  const [data, setData] = useState(null)
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    getData()
  }, [year, month])

  const getData = async () => {
    const signal = abortarPendentes()

    try {
      setLoading(true)
      setErrorMessage(null)
      setSelected(null)

      const { startDate, endDate } = CashFlow.monthRange(year, month)

      setData(await CashFlow.getDailyGoals({ businessId, startDate, endDate, requestAuth, signal }))
    } catch (error) {
      if (isCanceled(error)) return
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error, 'Não foi possível carregar as metas.'))
    } finally {
      if (!signal.aborted) setLoading(false)
    }
  }

  if (loading) {
    return (
      <LoadingContainer>
        <Loading/>
        <InfoText>Calculando metas...</InfoText>
      </LoadingContainer>
    )
  }

  if (errorMessage) {
    return (
      <LoadingContainer>
        <WarningText>{errorMessage}</WarningText>
      </LoadingContainer>
    )
  }

  const { startDate, endDate } = CashFlow.monthRange(year, month)

  // As categorias saem do mês inteiro: trocar de semana não pode fazer a escolhida sumir da lista.
  const monthCategories = CashFlow.buildGoalSections({
    data, days: CashFlow.daysOf(startDate, endDate), percentage
  }).categories

  if (monthCategories.length === 0) {
    return (
      <ExpensesContainer style={{ maxWidth: MAX_CONTENT_WIDTH, width: `calc(100% - ${SIDE_MARGIN * 2}px)` }}>
        <HintText>Nenhuma meta nem atendimento neste período.</HintText>
      </ExpensesContainer>
    )
  }

  const category = monthCategories.find(item => item.id === filter.categoryId) ?? monthCategories[0]

  const weeks = CashFlow.groupByWeek([], year, month)
  const current = selected ?? defaultWeek(weeks)
  const range = weeks.find(week => week.key === current)
  const days = CashFlow.daysOf(range.start, range.end)

  const weekCategory = CashFlow.buildGoalSections({ data, days, percentage })
    .categories.find(item => item.id === category.id)
  const weekSpecialties = weekCategory?.specialties ?? []
  const visibleCategory = weekSpecialties.length > 0 ? { ...weekCategory, specialties: weekSpecialties } : null

  const columns = days
    .map((day, index) => ({ ...day, index }))
    .filter(day => day.weekday !== SUNDAY || weekSpecialties.some(item => hasMovement(item.cells[day.index])))

  const weekTabs = weeks.map((week, index) => ({ label: `${index + 1}° Semana`, value: week.key }))

  const categoryOptions = monthCategories.map(item => ({ value: item.id, label: item.name }))

  return (
    <ExpensesContainer style={{ maxWidth: MAX_CONTENT_WIDTH, width: `calc(100% - ${SIDE_MARGIN * 2}px)` }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Select
          name='category'
          aria-label='Categoria'
          value={categoryOptions.find(option => option.value === category.id)}
          options={categoryOptions}
          placeholder='Categoria'
          onChange={(name, option) => onFilterChange({ categoryId: option.value })}
          containerStyles={{ width: SELECT_WIDTH }}
        />
      </div>
      <ItemTabs
        tabs={weekTabs}
        selectedTab={current}
        tabWidth={130}
        onTabChange={setSelected}
        containerStyle={{ padding: 0 }}
      />
      {visibleCategory === null ? (
        <HintText>Nenhuma meta nem atendimento nesta semana.</HintText>
      ) : children({ category: visibleCategory, columns })}
    </ExpensesContainer>
  )
}
